import React, { useEffect, useState } from 'react'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import CloseButton from "./components/Snackbar/closeIcon"
import { ACCESS_TOKEN, safeGetItem } from "./lib/storage"
import { AppRoutes, AuthRoutes } from "./navigation/routes"
import { Route, Routes, useLocation } from "react-router-dom"
import EsignDoc from "./containers/Esign/EsignDoc"


import {
  MyLoadingContextProvider
} from "./components/LoadingContextProvider/LoadingContextProvider"
import SuccessSigning from "./containers/SinglePages/SuccessSigning/SuccessSigning"
import ScrollToTopButton from "./components/ScrollTopButton/ScrollTopButton"
import BankAccount from "./containers/BankAccount/BankAccount"
import CreatePassword from "./containers/Password/Password"
import { UserContextProvider } from "./components/UserContext/UserContextProvider"
import SelfEnrollment from "./containers/SelfEnrollment/SelfEnrollment"
import PayLink from "./containers/PayLink/PayLink"
import { isPublicRoute } from "./lib/publicRoutes"
import useFeatureFlagsRefresh from "./hooks/useFeatureFlagsRefresh"
import useDocumentIdentity from "./hooks/useDocumentIdentity"
import useNeutralLogin from "./hooks/useNeutralLogin"
import NeutralBlank from "./components/NeutralAuth/NeutralBlank"
import usePortalBrand from "./hooks/usePortalBrand"
import { PortalBrandContext } from "./components/PortalBrand/PortalBrandContext"
import { StaffBrandContext, StaffBrandReportContext } from "./components/StaffBrand/StaffBrandContext"
import { LEGACY_PORTAL_BRAND } from "./components/PortalBrand/portalBrandStatus"
import { isPortalRole } from "./lib/portalAudience"
import { storedRole } from "./features/notifications/audience"
import DropdownSearchProvider from "./components/Dropdown/DropdownSearchProvider"
import UiKitProvider from "./components/UiKit/UiKitProvider"

const UiKitCatalog = process.env.NODE_ENV === 'development'
  ? require('./containers/UiKitCatalog/UiKitCatalog').default
  : null


function App() {
  const token = safeGetItem(ACCESS_TOKEN)
  const [loggedIn, setLoggedIn] = useState(!!token)
  const [role] = useState(storedRole)
  const location = useLocation()

  const isPublic = isPublicRoute(location.pathname)

  useFeatureFlagsRefresh()

  const { ready: preAuthReady, neutral: neutralLogin } = useNeutralLogin()
  const preAuth = !loggedIn || isPublic

  const portalBrand = usePortalBrand({ active: loggedIn && !isPublic && isPortalRole(role) })
  const [sidebarBrand, setSidebarBrand] = useState(null)
  const staffArea = loggedIn && !isPublic && !isPortalRole(role)
  const staffBrand = staffArea ? sidebarBrand : null

  useDocumentIdentity({
    settled: preAuthReady,
    neutral: neutralLogin,
    preAuth,
    pathname: location.pathname,
    portalBrand,
    staffBrand,
    signingBrand: location.state?.firmBrand,
  })

  const whenReady = (element) => (preAuthReady ? element : <NeutralBlank />)

  useEffect(() => {
    if (!token) {
      setLoggedIn(false)
    }
  }, [token])
  return (
    <PortalBrandContext.Provider value={portalBrand || LEGACY_PORTAL_BRAND}>
      <StaffBrandContext.Provider value={staffBrand}>
        <StaffBrandReportContext.Provider value={setSidebarBrand}>
          <MyLoadingContextProvider>
            <UserContextProvider>
              <UiKitProvider>
              <DropdownSearchProvider staffArea={staffArea}>
                <Routes>
                  <Route path="/esign-doc" element={<EsignDoc />} />
                  <Route path="/esign-doc/:accessUUID" element={<EsignDoc />} />
                  <Route path="/bank-account/:userId" element={<BankAccount />} />
                  <Route path="/create-password/:type/:userId" element={whenReady(<CreatePassword neutral={neutralLogin} />)} />
                  <Route path="/success/signing" element={<SuccessSigning />} />
                  <Route path="/enrollment/:affiliateId" element={<SelfEnrollment />} />
                  <Route path="/pay" element={<PayLink />} />
                  <Route path="/pay/:token" element={<PayLink />} />
                  {UiKitCatalog && <Route path="/ui-kit" element={<UiKitCatalog />} />}
                </Routes>

                {!isPublic && (loggedIn ? <AppRoutes /> : whenReady(<AuthRoutes neutral={neutralLogin} setForgotPassword={() => {
                }} />))}
              </DropdownSearchProvider>
              </UiKitProvider>
              <ToastContainer
                className="containerToast"
                position="top-right"
                autoClose={3000}
                icon={false}
                hideProgressBar={true}
                newestOnTop={false}
                closeOnClick={false}
                pauseOnFocusLoss
                draggable={false}
                pauseOnHover
                closeButton={CloseButton}
              />
              <ScrollToTopButton />
            </UserContextProvider>
          </MyLoadingContextProvider>
        </StaffBrandReportContext.Provider>
      </StaffBrandContext.Provider>
    </PortalBrandContext.Provider>
  )
}

export default App
